import { Box, Link } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import BcoHeader from "shared/components/BcoHeader";

import { FORGOTPASSWORD, SIGNUP } from "config/routes";
import { generateVanillaAuthUrl, setAuthCookies } from "helpers/auth";
import { IToken, cognitoSignupConfirmation } from "pages/Cognito/services";
import { useNavigate, useSearchParams } from "react-router-dom";
import TextButton from "shared/components/TextButton";
import TextInputControlled from "shared/components/form/TextInputControlled";
import { Error, FlexLayout, GridColumnLayout, Text } from "shared/globalStyles";
import { useCheckMobile } from "shared/hooks/useCheckMobile";
import { IAWSResponse, cognitoSignIn } from "./services";
import { LoginWrapper } from "./styles";
import { getLangFromQuery } from "helpers/getLangFromQuery";

interface formValues {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const verificationCode = searchParams.get("token");
  const {
    t,
    i18n: { language, changeLanguage },
  } = useTranslation("global");
  const navigate = useNavigate();
  const isMobile = useCheckMobile();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isConfirmSignUpError, setConfirmSignUpError] = useState<boolean>(false);
  const { handleSubmit, control, formState } = useForm<formValues>();
  const { errors } = formState;

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const target = params.get("target");
  const locale = getLangFromQuery(search) ?? language;

  const postLoginRedirect = useCallback(
    async (res: IAWSResponse) => {
      const targetCMS =
        target?.includes(process.env.REACT_APP_AWS_CMS?.replace(/^(https?:\/\/)/, "") as string) ||
        target?.includes("localhost.breastcancer.org");

      if (targetCMS) {
        const redirectionUrlScheme = target?.includes("localhost.breastcancer.org")
          ? "http"
          : "https";

        const cmsUrl = target
          ? `${redirectionUrlScheme}://${target}`
          : process.env.REACT_APP_AWS_CMS;

        let redirectionToCMS = `${cmsUrl}/login/connect?logged=true`;
        if (getLangFromQuery(search)) redirectionToCMS += `&locale=${locale}`;

        window.location.replace(redirectionToCMS);
      } else {
        const redirectionToVanilla = await generateVanillaAuthUrl(
          res.data.IdToken,
          `${process.env.REACT_APP_VANILLA_JWT_URL}/${language}`,
          language,
        );

        window.location.replace(redirectionToVanilla);
      }
    },
    [locale, language, search, target],
  );

  const onSubmit = useCallback(
    async (value: FieldValues) => {
      try {
        setIsError(false);
        setConfirmSignUpError(false);
        setIsLoading(true);

        const { username, password } = value;
        const res = await cognitoSignIn(username, password, language);
        if (res?.data) {
          if (verificationCode) {
            const confirmationCode: IToken = {
              accessToken: res.data.AccessToken,
              confirmationCode: verificationCode,
              locale: locale,
            };
            await cognitoSignupConfirmation(confirmationCode, res.data.AccessToken);
          }

          setAuthCookies(res.data.AccessToken, res.data.RefreshToken, res.data.IdToken);
          postLoginRedirect(res);
        }

        setIsLoading(false);
      } catch (error: unknown) {
        if (
          typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof (error as { message?: unknown }).message === "string"
        ) {
          const message = (error as { message: string }).message;
          console.warn(error, "==== Error in log in");

          if (message.includes("CONFIRM_SIGN_UP_EXCEPTION")) {
            setConfirmSignUpError(true);
          }
        } else {
          console.warn(error, "==== Unknown error in log in");
        }

        setIsLoading(false);
        setIsError(true);
      }
    },
    [language, verificationCode, locale, postLoginRedirect],
  );

  useEffect(() => {
    const langFromQuery = getLangFromQuery(search);
    changeLanguage(langFromQuery);

    const redirectToLang = (lang?: string) => {
      const selectedLang = lang ?? getLangFromQuery(search);
      const newParams = new URLSearchParams();

      newParams.set("locale", selectedLang);
      if (target) {
        newParams.set("target", target);
      }

      navigate({
        search: `?${newParams.toString()}`,
      });
    };

    if (target?.includes("/es/") && langFromQuery !== "es") {
      redirectToLang("es");
    }
  }, [search, target, changeLanguage, navigate]);

  useEffect(() => {
    changeLanguage(locale);
    const keyDownHandler = (ev: KeyboardEvent) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [changeLanguage, locale, handleSubmit, onSubmit]);

  const fontSizeTitle = isMobile ? "36px" : "60px";

  return (
    <>
      <LoginWrapper onSubmit={handleSubmit(onSubmit)}>
        <BcoHeader />
        <GridColumnLayout padding="50px 0 0 0">
          <FlexLayout flexDirection="column">
            <Text fontsize={fontSizeTitle}>
              {t("login.loginTitle")}
              <br />
              <Text fontStyle="italic" fontsize={fontSizeTitle}>
                {t("login.community")}
              </Text>
            </Text>
            <FlexLayout padding="1vh 0 0 0">
              <Text fontFamily="Larsseit">{t("login.notMember")}</Text> &nbsp;
              <Link
                component="button"
                variant="body2"
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate({
                    pathname: SIGNUP,
                    search: getLangFromQuery(search),
                  });
                }}
              >
                <Text fontFamily="Larsseit"> {t("login.joinNow")}</Text>
              </Link>
            </FlexLayout>
            <FlexLayout padding="1vh 0 0 0">
              <Text fontFamily="Larsseit">{t("signUp.helpText")}</Text>
              &nbsp;
              <Link variant="body2" href={`mailto:${t("signUp.helpEmail")}`} target="_blank">
                <Text fontFamily="Larsseit">{t("signUp.helpLinkText")}</Text>
              </Link>
            </FlexLayout>
          </FlexLayout>
          <FlexLayout flexDirection="column" padding={isMobile ? "2vh 0 0 0" : "0px"}>
            {isError && !isConfirmSignUpError && (
              <Error fontsize="16px">{t("error.CognitoSignIn")}</Error>
            )}
            {isError && isConfirmSignUpError && (
              <Error fontsize="16px">{t("error.ConfirmSignUp")}</Error>
            )}
            <br />
            <TextInputControlled
              control={control}
              name={"username"}
              required={true}
              error={errors?.username ? true : false}
              label={t("login.usernameOrEmail")}
            />
            <Box mt={6} />
            <TextInputControlled
              control={control}
              name={"password"}
              type="password"
              required={true}
              error={errors?.password ? true : false}
              label={t("login.password")}
            />
            <FlexLayout padding="2vh 0 0 0">
              <Text fontFamily="Larsseit">{t("login.forgotPassword")}</Text> &nbsp;
              <Link
                component="button"
                variant="body2"
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate({
                    pathname: FORGOTPASSWORD,
                    search: getLangFromQuery(search),
                  });
                }}
              >
                <Text fontFamily="Larsseit"> {t("login.resetPassword")}</Text>
              </Link>
            </FlexLayout>
            <Box mt={8} />
            <TextButton
              fullWidth={false}
              variant="contained"
              type="submit"
              isLoadingButton={isLoading}
              text={t("login.signIn").toString()}
            />
          </FlexLayout>
        </GridColumnLayout>
      </LoginWrapper>
    </>
  );
};

export default Login;
